import React, { useState } from 'react';
import SiteNav from '@/components/SiteNav';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { Globe, ArrowLeft, Mail, CheckCircle } from 'lucide-react';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [debugToken, setDebugToken] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await api.forgotPassword(email);
      setSent(true);
      if (res.data.debug_token) setDebugToken(res.data.debug_token);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send reset link');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center px-6 pt-24 pb-10" data-testid="forgot-password-page">
      <SiteNav />
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-6 min-h-[44px]">
            <div className="w-10 h-10 bg-indigo-500 rounded-xl flex items-center justify-center">
              <Globe className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>EasyPeazy</span>
          </Link>
          <h1 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit' }}>Reset Password</h1>
          <p className="text-slate-400">Enter your email to receive a reset link</p>
        </div>

        {!sent ? (
          <form onSubmit={handleSubmit} className="glass-card p-8 space-y-5">
            {error && <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-lg text-sm" data-testid="forgot-error">{error}</div>}

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Email Address</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-slate-900/50 border border-slate-800 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 rounded-lg h-12 px-4 text-slate-100 placeholder:text-slate-600 transition-all outline-none"
                placeholder="you@example.com"
                required
                data-testid="forgot-email-input"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-indigo-500 hover:bg-indigo-600 text-white h-12 rounded-lg font-medium transition-all active:scale-95 disabled:opacity-50 shadow-lg shadow-indigo-500/20"
              data-testid="forgot-submit-btn"
            >
              {loading ? 'Sending...' : 'Send Reset Link'}
            </button>

            <p className="text-center text-sm text-slate-500">
              <Link to="/login" className="text-indigo-400 hover:text-indigo-300 inline-flex items-center gap-1 min-h-[44px]">
                <ArrowLeft className="w-3 h-3" /> Back to Login
              </Link>
            </p>
          </form>
        ) : (
          <div className="glass-card p-8 text-center">
            <CheckCircle className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-white mb-2" style={{ fontFamily: 'Outfit' }}>Check Your Email</h3>
            <p className="text-slate-400 mb-4">If an account exists for {email}, we've sent a password reset link.</p>

            {debugToken && (
              <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 mb-4 text-left">
                <p className="text-xs text-amber-400 font-medium mb-1">Dev Mode - Email not configured yet</p>
                <p className="text-xs text-slate-400 mb-2">Use this link to reset your password:</p>
                <Link
                  to={`/reset-password?token=${debugToken}`}
                  className="text-xs text-indigo-400 hover:text-indigo-300 break-all font-mono"
                  data-testid="debug-reset-link"
                >
                  /reset-password?token={debugToken}
                </Link>
              </div>
            )}

            <Link to="/login" className="text-indigo-400 hover:text-indigo-300 text-sm inline-flex items-center gap-1 min-h-[44px]">
              <ArrowLeft className="w-3 h-3" /> Back to Login
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
